/**
 * Observability policy — shared FM + BH.
 * Separa dominios y define qué persiste vs qué queda solo en runtime logs.
 */

export type ObservabilityDomain =
  | "audit"
  | "error"
  | "warning"
  | "business"
  | "trace"
  | "metric"
  | "debug";

export type StructuraApp = "fm" | "bh";

/** Retención sugerida (días). Aplicar vía cron de purge. */
export const OBSERVABILITY_RETENTION_DAYS: Record<string, number | "permanent"> = {
  audit: 730,
  payment: "permanent",
  error: 90,
  warning: 90,
  trace: 14,
  business: 365,
  metric: "permanent",
  debug: 1,
};

/** Eventos de negocio que SÍ persisten (allowlist). */
export const BUSINESS_EVENT_ALLOWLIST = new Set([
  "onboarding_started",
  "onboarding_completed",
  "session_started",
  "page_view",
  "product_viewed",
  "category_viewed",
  "search",
  "search_zero",
  "add_to_cart",
  "remove_from_cart",
  "scroll_depth",
  "time_on_page",
  "page_exit",
  "bounce",
  "hero_cta_clicked",
  "banner_clicked",
  "carousel_slide_viewed",
  "wishlist_opened",
  "cart_updated",
  "checkout_started",
  "bundle_started",
  "bundle_step_viewed",
  "bundle_step_completed",
  "bundle_step_changed",
  "bundle_rule_triggered",
  "bundle_abandoned",
  "bundle_completed",
  "bundle_checkout",
  "bundle_conversion",
  "flow_started",
  "flow_step_viewed",
  "flow_step_completed",
  "flow_abandoned",
  "flow_completed",
  "flow_checkout",
  "flow_conversion",
  "checkout_completed",
  "tenant_created",
  "order_created",
  "order_delivered",
  "review_requested",
  "review_submitted",
  "review_link_clicked",
  "nps_submitted",
  "loyalty_redeemed",
  "subscription_activated",
  "payment_attempted",
  "payment_failed",
  "payment_failed_spike",
  "repurchase_top_sku_sent",
  "qr_mesa_scanned",
  "payment_confirmed",
  "provisioning_started",
  "provisioning_failed",
  "provisioning_completed",
  "shipping.quote",
  "shipping.create",
  "shipping.track",
  "shipping.label",
  "shipping.error",
  "shipping.webhook",
  "delivery_requested",
  "delivery_created",
  "driver_assigned",
  "driver_arrived",
  "delivery_started",
  "delivery_completed",
  "delivery_cancelled",
  "delivery_failed",
  "delivery_timeout",
  "media_uploaded",
  "media_deleted",
  "media_linked",
  "media_unlinked",
  "orphan_detected",
  "upload_failed",
  "quota_exceeded",
  "import_failed",
  "legacy_image_uploaded",
]);

/** Embudo storefront nativo (B2C / widget). */
export const STOREFRONT_FUNNEL_EVENTS = [
  "session_started",
  "page_view",
  "product_viewed",
  "category_viewed",
  "search",
  "search_zero",
  "add_to_cart",
  "remove_from_cart",
  "scroll_depth",
  "time_on_page",
  "page_exit",
  "bounce",
  "hero_cta_clicked",
  "banner_clicked",
  "carousel_slide_viewed",
  "wishlist_opened",
  "cart_updated",
  "checkout_started",
  "checkout_completed",
] as const;

export type StorefrontFunnelEvent = (typeof STOREFRONT_FUNNEL_EVENTS)[number];

export function isStorefrontFunnelEvent(event: string): event is StorefrontFunnelEvent {
  return (STOREFRONT_FUNNEL_EVENTS as readonly string[]).includes(event);
}

/** Prefijos/nombres de warning que SÍ persisten (anomalías operativas). */
const WARNING_PERSIST_PATTERNS: RegExp[] = [
  /\.retry$/,
  /\.duplicate$/,
  /duplicate_/,
  /_retry$/,
  /\.slow$/,
  /partial_failure/,
  /stock\.mismatch/,
  /webhook\.(duplicate|error|failed)/,
  /payment\.(duplicate|failed|timeout)/,
  /provider\.(slow|timeout|partial)/,
];

export interface ObservabilityContext {
  app: StructuraApp;
  environment: string;
  releaseVersion: string;
  releaseChannel: string;
  releaseCommit?: string | null;
  tenantId?: string | null;
  workspaceId?: string | null;
  actorUserId?: string | null;
  requestId?: string;
  correlationId?: string;
}

export interface ObservabilityEnvelope extends ObservabilityContext {
  domain: ObservabilityDomain;
  event: string;
  level: "info" | "warn" | "error";
  ts: string;
  meta?: Record<string, unknown>;
}

export function isBusinessEvent(event: string): boolean {
  if (BUSINESS_EVENT_ALLOWLIST.has(event)) return true;
  if (/^(payment_|provisioning_|whatsapp_|shipping_|shipnow\.|media_)/.test(event)) return true;
  return false;
}

export function shouldPersistWarning(event: string): boolean {
  if (WARNING_PERSIST_PATTERNS.some((re) => re.test(event))) return true;
  if (event.includes("webhook") && (event.includes("duplicate") || event.includes("failed"))) return true;
  return false;
}

export function shouldPersistDebug(environment: string): boolean {
  const env = environment.toLowerCase();
  return env === "development" || env === "local" || env === "test";
}

/** Sampling para request traces — NO persistir todos los requests. */
export function shouldSampleTrace(opts: {
  failed?: boolean;
  slowMs?: number;
  slowThresholdMs?: number;
  paymentRelated?: boolean;
  path?: string;
}): boolean {
  const slowThreshold = opts.slowThresholdMs ?? 2000;
  if (opts.failed) return true;
  if (opts.paymentRelated) return true;
  if (opts.slowMs != null && opts.slowMs >= slowThreshold) return true;
  if (opts.path && /\/api\/(payments|billing|self-service|webhooks)/.test(opts.path)) return true;
  return Math.random() < 0.03;
}

export function resolveObservabilityDomain(
  level: "info" | "warn" | "error",
  event: string,
): ObservabilityDomain {
  if (level === "error") return "error";
  if (level === "warn") return "warning";
  if (isBusinessEvent(event)) return "business";
  if (event.startsWith("trace.") || event.includes(".request.")) return "trace";
  if (event.startsWith("debug.")) return "debug";
  return "trace";
}

export function shouldPersistDomain(
  domain: ObservabilityDomain,
  event: string,
  environment: string,
): boolean {
  switch (domain) {
    case "audit":
      return true;
    case "error":
      return true;
    case "warning":
      return shouldPersistWarning(event);
    case "business":
      return isBusinessEvent(event);
    case "trace":
      return false;
    case "metric":
      return true;
    case "debug":
      return shouldPersistDebug(environment);
    default:
      return false;
  }
}

export function buildObservabilityEnvelope(input: {
  domain: ObservabilityDomain;
  level: "info" | "warn" | "error";
  event: string;
  ctx: ObservabilityContext;
  meta?: Record<string, unknown>;
}): ObservabilityEnvelope {
  return {
    ...input.ctx,
    domain: input.domain,
    event: input.event,
    level: input.level,
    ts: new Date().toISOString(),
    meta: input.meta,
  };
}
